import { useCallback, useSyncExternalStore } from 'react';
import { useCurrentUser } from '@/features/auth/useCurrentUser';

const KEY = 'tap.board.fullView';

/**
 * Whether an admin is looking at the whole company or only at their own cards.
 *
 * An admin is two people at once: the person who runs the board, and an employee with
 * work assigned to them. Those want different screens — "what is everyone doing" and
 * "what do I have to do today" — and defaulting to the first means an admin's own
 * cards are buried among everyone else's, unread dots included.
 *
 * A per-device preference rather than a column on `users`: it is a way of looking, not
 * a fact about the person, and it needs no migration to exist. Someone on a laptop and
 * a phone can reasonably want different answers on each.
 *
 * Module scope, mirroring useTheme, because three separate subtrees read this — the
 * board, the toolbar switch, and the realtime subscription up in the shell. Three
 * useState copies would mean three answers, and the one that matters is the query key:
 * if the board and the realtime handler disagree about it, cards stop updating.
 */
let current = readStored();
const listeners = new Set<() => void>();

function readStored(): boolean {
  try {
    // Default ON, so nothing changes for an admin who never touches the switch.
    return localStorage.getItem(KEY) !== '0';
  } catch {
    // Private-mode Safari throws on localStorage.
    return true;
  }
}

export function setFullView(value: boolean): void {
  current = value;
  try {
    localStorage.setItem(KEY, value ? '1' : '0');
  } catch {
    // A remembered preference is a nicety; the session still works without it.
  }
  listeners.forEach((notify) => notify());
}

export interface BoardViewState {
  /** Only an admin has two views to choose between. */
  canSwitch: boolean;
  /** True when the board is showing everyone. Always true for a non-admin's own board. */
  fullView: boolean;
  /**
   * Sent to the API when narrowed. Undefined means "everything I may see", which the
   * server already scopes — an employee cannot widen their board by omitting it.
   */
  assigneeId: string | undefined;
  /** Query-key segment. The board and the realtime handler must agree on it. */
  scope: string;
  toggle: () => void;
}

export function useBoardView(): BoardViewState {
  const { data: me } = useCurrentUser();

  const stored = useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    () => current,
    () => true,
  );

  const toggle = useCallback(() => setFullView(!current), []);

  const canSwitch = me?.isAdmin ?? false;
  // Everyone else sees their own cards and nothing else, so the switch is moot: their
  // "full view" already is their own board.
  const fullView = canSwitch ? stored : true;
  const assigneeId = canSwitch && !fullView ? me?.id : undefined;

  return { canSwitch, fullView, assigneeId, scope: assigneeId ?? 'me', toggle };
}
